import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  Button,
  Typography,
  MenuItem,
  Select,
  Input,
  InputLabel,
  ListItemText,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import applovinLogo from "../../images/network/IconsThin/applovin-icon.png";
import facebookLogo from "../../images/network/IconsThin/facebook-icon.png";
import googleLogo from "../../images/network/IconsThin/googleads-icon.png";
import ironsourceLogo from "../../images/network/IconsThin/ironsource-icon.png";
import mintegralLogo from "../../images/network/IconsThin/mintegral-icon.png";
import unityLogo from "../../images/network/IconsThin/unityads-icon.png";
import vungleLogo from "../../images/network/IconsThin/vungle-icon.png";
import tiktokLogo from "../../images/network/IconsThin/tiktok-icon.png";
import playinIcon from "../../images/network/IconsThin/playin-icon.png";
import molocoIcon from "../../images/network/IconsThin/moloco-icon.png";
import appgrowthIcon from "../../images/network/IconsThin/appgrowth-icon.png";

import { getServerUri } from "../modules/uriUtility";
import { incrementCompanyUsage } from "../modules/companySubscriptionApi";
import DialogDropboxSubmit from "./dialogDropboxSubmit";
import DialogPlayableSizeWarning from "./dialogPlayableSizeWarning";
import { MAX_PLAYABLE_SIZE_BYTES } from "../modules/playableSizeCalculator";
import {
  deliversToDropbox,
  dropboxDestination,
  noFolderCancelledMessage,
  resolveExportGameTitle,
  usesGameTitleInExportNaming,
} from "../modules/buildProfileUtils";
import _ from "lodash";

const networkViews = [
  { name: "default", logo: playinIcon, caption: "Test Playable" },
  { name: "applovin", logo: applovinLogo, caption: "AppLovin" },
  { name: "facebook", logo: facebookLogo, caption: "Facebook" },
  { name: "google", logo: googleLogo, caption: "Google" },
  { name: "ironsource", logo: ironsourceLogo, caption: "IronSource" },
  { name: "mintegral", logo: mintegralLogo, caption: "Mintegral" },
  { name: "unity", logo: unityLogo, caption: "Unity" },
  { name: "liftoff", logo: vungleLogo, caption: "Liftoff" },
  { name: "tiktok", logo: tiktokLogo, caption: "Tiktok" },
  { name: "moloco", logo: molocoIcon, caption: "Moloco" },
  { name: "appgrowth", logo: appgrowthIcon, caption: "AppGrowth" },

];

const useStyles = makeStyles(() => ({
  networkLogo: { height: 20 },
  networkDiv: { display: "flex", alignItems: "center", padding: "2px 10px 2px 0px" },
  languageSelector: { width: "100%" },
  downloadDialogCard: { margin: "0px 10px 10px 10px", padding: 0, width: 300, backgroundColor: "#363636" },
}));

const DOWNLOAD_ERROR_MESSAGE = "Error During Downloading HTMLs";

/**
 *
 * @param props.open
 * @param props.isOpen
 * @param props.onClose
 * @param props.selectedVersion
 * @param props.selectedBase
 * @param props.setIsDownloadingPlayable
 * @returns {JSX.Element}
 */
export default function dialogVersionDownload(props) {
  const [networks, setNetworks] = useState(new Set());
  const [newNetworks, setNewNetworks] = useState(new Set());

  const [totalSet, setTotalSet] = useState(new Set());

  const [isBuilding, setIsBuilding] = useState(false);
  const [buttonText, setButtonText] = useState("Download");
  const [error, setError] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);
  const [isAllLanguagesSelected, setIsAllLanguagesSelected] = useState(false);
  const [gameTitleOverride, setGameTitleOverride] = useState("");
  const classes = useStyles();

  const buildProfileData = props.userCompany?.buildProfileData;
  // Submitting is its own action, confirmed each time: a download can be
  // thrown away, a file in the client's Dropbox cannot.
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  // The oversize warning interrupts an export that is already headed
  // somewhere, so it remembers where: resuming must not turn a submit into a
  // plain download.
  const [sizeWarningVersions, setSizeWarningVersions] = useState([]);
  const [sizeWarningToDropbox, setSizeWarningToDropbox] = useState(false);
  const [sizeWarningFolder, setSizeWarningFolder] = useState("");
  const canDeliverDropbox = deliversToDropbox(buildProfileData);
  const isGameTitleEditable = usesGameTitleInExportNaming(buildProfileData);
  const exportGameTitle = resolveExportGameTitle(buildProfileData, gameTitleOverride, props.selectedGame?.title);

  useEffect(() => {
    if (props.isOpen) setGameTitleOverride(props.selectedGame?.title || "");
  }, [props.isOpen, props.selectedGame?.title]);

  function changeNetwork(event) {
    const newNetwork = new Set(networks);
    const newNetwork2 = new Set(newNetworks);
    const newTotalNetwork = new Set(totalSet);
    if (event.target.checked) {
      if (event.target.name != "default") {
        newNetwork.add(event.target.name);
      } else {
        newNetwork2.add(event.target.name);
      }
      newTotalNetwork.add(event.target.name);
    } else {
      newNetwork2.delete(event.target.name);
      newNetwork.delete(event.target.name);
      newTotalNetwork.delete(event.target.name);
    }
    setNetworks(newNetwork);
    setNewNetworks(newNetwork2);
    setTotalSet(newTotalNetwork);
  }

  /**
   *
   * @param buildsToDo
   * @param baseDir
   * @param timeStamp
   * @param version
   * @param versionName
   * @param onCompleteCallback
   */
  function buildGamesWithRecursive(buildsToDo, baseDir, timeStamp, version, versionName, onCompleteCallback, noFolderCancelled = []) {
    if (buildsToDo.length === 0) {
      const message = noFolderCancelledMessage(noFolderCancelled);
      if (message) props.createSnackbar?.(message, "warning", false);
      onCompleteCallback();
    } else {
      const buildRecord = buildsToDo.shift();
      fetch(getServerUri("version/buildGame"), {
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          accessToken: localStorage.getItem("session.accessToken"),
          baseDir,
          buildRecord: JSON.stringify(buildRecord),
          buildProfileData: JSON.stringify(props.userCompany?.buildProfileData || {}),
          gameTitle: exportGameTitle,
          baseTitle: props.selectedBase.title,
          timeStamp,
          version: JSON.stringify(version),
          versionName,
        }),
      }).then((response) => {
        if (response.status === 200) {
          response
            .json()
            .catch(() => ({}))
            .then((body) => {
              noFolderCancelled.push(...(body?.noFolderCancelledNetworks || []));
              buildGamesWithRecursive(buildsToDo, baseDir, timeStamp, version, versionName, onCompleteCallback, noFolderCancelled);
            });
        } else {
          // The server refused or failed this build (e.g. BUILD_PROFILE_MISSING).
          // Stop here and show why — the remaining builds would fail the same way.
          readServerError(response).then((message) => {
            console.error("buildGame failed:", message);
            setError(message);
            setIsBuilding(false);
            props.setIsDownloadingPlayable(false);
          });
        }
      });
    }
  }

  /** The message a failed export response carries, in the shape the server sends it. */
  function readServerError(response) {
    return response
      .json()
      .then((body) => body?.detail || body?.message || DOWNLOAD_ERROR_MESSAGE)
      .catch(() => DOWNLOAD_ERROR_MESSAGE);
  }

  function updatePlayableCounter(totalSet, selectedLanguages, onCompleteCallback) {
    const counters = {};
    const createdPlayables = networks.size * selectedLanguages.length;
    const testPlayables = (newNetworks.size > 0 ? 1 : 0) * selectedLanguages.length;
    if (createdPlayables > 0) counters.monthlyCreatedPlayables = createdPlayables;
    if (testPlayables > 0) counters.testPlayableCount = testPlayables;

    if (_.isEmpty(counters)) {
      onCompleteCallback();
      return Promise.resolve();
    }

    return incrementCompanyUsage(_.get(props.userCompany, "_id"), counters)
      .then((newCompany) => props.setUserCompany(newCompany))
      .catch((err) => {
        console.log("createdPlayables update failed: " + (err && err.message));
      })
      // The build is already delivered; a missed count must not hold it back.
      .then(() => onCompleteCallback());
  }

  if (props.selectedBase == null) {
    window.location.reload();
  }
  if (!props.selectedVersion === undefined) return null;
  /**
   * Everything that has to be true before a build starts, reported the way
   * the Download button has always reported it.
   *
   * Shared with Submit so the two refuse for the same reasons at the same
   * moment: asking "send to Dropbox?" about an export that cannot be built
   * is a question nobody can answer usefully.
   */
  function canStartExport() {
    if (
      props.userCompany.subscription.periods[props.userCompany.subscription.periods.length - 1]
        .monthlyCreatedPlayables +
      networks.size * selectedLanguages.length >
      props.plans[props.getPlanIndex(props.userCompany.subscription.currentPlan)].numberOfPlayables
    ) {
      props.createSnackbar(
        `Your company's current plan allows up to ${props.plans[props.getPlanIndex(props.userCompany.subscription.currentPlan)].numberOfPlayables
        } playables`,
        "warning"
      );
      return false;
    }
    if (
      props.userCompany.subscription.periods[props.userCompany.subscription.periods.length - 1]
        .testPlayableCount +
      newNetworks.size * selectedLanguages.length >
      props.plans[props.getPlanIndex(props.userCompany.subscription.currentPlan)].numberOfTests
    ) {
      props.createSnackbar(
        `Your company's current plan allows up to ${props.plans[props.getPlanIndex(props.userCompany.subscription.currentPlan)].numberOfTests
        } playables`,
        "warning"
      );
      return false;
    }
    if (networks.size === 0 && newNetworks.size === 0) {
      setError("Network was not selected");
      return false;
    }
    if (selectedLanguages.length === 0) {
      setError("No languages were selected.");
      return false;
    }
    return true;
  }

  /**
   * Build this version and either hand it back as a download or send it to
   * the client's Dropbox.
   *
   * One function for both, with the destination as an argument rather than
   * read from state: the Submit button decides it as it calls, and a state
   * write would not have landed yet.
   */
  /** The version, when its estimate is over the networks' limit. */
  function oversizedVersions() {
    const size = Number(props.totalSizeValue) || 0;
    if (size < MAX_PLAYABLE_SIZE_BYTES) return [];
    return [{
      versionID: props.selectedVersion?._id,
      versionName: props.selectedVersion?.title,
      size,
    }];
  }

  function startExport(toDropbox, dropboxFolder) {
    if (!canStartExport()) return;

    const oversized = oversizedVersions();
    if (oversized.length > 0) {
      setSizeWarningToDropbox(toDropbox);
      setSizeWarningFolder(dropboxFolder);
      setSizeWarningVersions(oversized);
      return;
    }
    runExport(toDropbox, dropboxFolder);
  }

  function runExport(toDropbox, dropboxFolder) {
    setError(null);
    setIsBuilding(true);
    setButtonText("Building...");
    console.info("sending download request");
    let networkArray = Array.from(totalSet);
    console.info("networks: " + JSON.stringify(networkArray));
    props.setIsDownloadingPlayable(true);
    fetch(getServerUri("version/preBuildGame"), {
      method: "POST",
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        accessToken: localStorage.getItem("session.accessToken"),
        versionID: String(props.selectedVersion._id),
        networks: networkArray,
        languages: selectedLanguages,
      }),
    })
      .then((response) => {
        if (response.status === 200) {
          response.json().then((dataPreBuild) => {
            // console.log({ dataPreBuild });
            const buildsToDo = _.clone(dataPreBuild.buildsToDo);
            if (buildsToDo.length < 1) {
              console.error("There is no build to do");
              return;
            }
            const versionName = dataPreBuild.version.title.replaceAll(" ", "_");
            const baseDir = dataPreBuild.baseDir;
            const timeStamp = dataPreBuild.timeStamp;
            const version = dataPreBuild.version;
            buildGamesWithRecursive(buildsToDo, baseDir, timeStamp, version, versionName, () => {
              fetch(getServerUri("version/uploadGame"), {
                method: "POST",
                headers: {
                  "content-type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({
                  accessToken: localStorage.getItem("session.accessToken"),
                  baseDir,
                  baseID: dataPreBuild.baseID,
                  companyName: dataPreBuild.companyName,
                  gameID: dataPreBuild.gameID,
                  requestedSupportedLanguages: JSON.stringify(dataPreBuild.requestedSupportedLanguages),
                  requestedSupportedNetworks: JSON.stringify(dataPreBuild.requestedSupportedNetworks),
                  timeStamp,
                  versionName,
                  buildProfileData: JSON.stringify(props.userCompany?.buildProfileData || {}),
                  gameTitle: exportGameTitle,
                  baseTitle: props.selectedBase.title,
                  // usage analytics: which version, from which dialog
                  versionID: String(version._id),
                  exportSource: "single",
                  deliverToDropbox: String(toDropbox),
                  ...(dropboxFolder ? { dropboxFolder } : {}),
                }),
              })
                .then((response) => {
                  if (response.status === 200) {
                    response.json().then((obj) => {
                      if (_.has(obj, "completion")) {
                        console.info("Progress Received: ", obj);
                      } else if (_.has(obj, "file")) {
                        // Through the snackbar, not the dialog: this
                        // closes itself a few lines down.
                        if (obj.dropbox) props.createSnackbar?.(obj.dropbox, "info", false);
                        setButtonText("Download");
                        setIsBuilding(false);
                        props.setIsDownloadingPlayable(false);
                        // updateDefaultPlayableCounter(newNetworks.size * selectedLanguages.length).catch((err) => {
                        // });
                        setTotalSet(new Set());
                        //new added
                        setNetworks(new Set());
                        setNewNetworks(new Set());
                        props.setIsDownloadDialogOpen(false);
                        updatePlayableCounter(totalSet, selectedLanguages, () => {
                          // A submitted export went to Dropbox; nothing should
                          // land in the person's Downloads folder as well.
                          if (!toDropbox) window.location = obj.file;
                        }).catch((err) => {
                          console.log(err);
                          setError(DOWNLOAD_ERROR_MESSAGE);
                        });
                      }
                    });
                  } else {
                    props.setIsDownloadingPlayable(false);
                    response
                      .json()
                      .then((object) => {
                        setError("Server did not process the request");
                        console.log("non 200 response:");
                        console.dir(object);
                        setTotalSet(new Set());
                        //new added
                        setNetworks(new Set());
                        setNewNetworks(new Set());
                      })
                      .catch((err) => {
                        console.log("can't parse response as json", err);
                        setError(DOWNLOAD_ERROR_MESSAGE);
                      });
                  }
                })
                .catch((err) => {
                  props.setIsDownloadingPlayable(false);
                  setError("Server did not process the request");
                  setError(err);
                  console.log(err);
                });
            });
          });
        } else {
          props.setIsDownloadingPlayable(false);
          response
            .json()
            .then((object) => {
              setError("Server did not process the request");
              console.log("non 200 response:");
              console.dir(object);
              setTotalSet(new Set());
              //new added
              setNetworks(new Set());
              setNewNetworks(new Set());
            })
            .catch((err) => {
              console.log("can't parse response as json", err);
              setError(DOWNLOAD_ERROR_MESSAGE);
            });
        }
      })
      .catch((err) => {
        props.setIsDownloadingPlayable(false);
        setError("Could not send request to server");
        console.error("FETCH ERROR");
        console.error(err);
      });
  }
  return (
    <Dialog open={props.isOpen} onClose={props.onClose}>
      <DialogTitle style={{ textAlign: "center" }}>Download Creative</DialogTitle>
      <Card className={classes.downloadDialogCard}>
        <CardContent style={{ padding: 10 }}>
          <Typography>Version: {String(_.get(props, "selectedVersion.title"))}</Typography>
          <Typography> {`Estimated Size: ${(props.totalSizeValue / 1024).toFixed(2)} kb`} </Typography>

          {isGameTitleEditable && (
            <div style={{ marginTop: 10 }}>
              <InputLabel htmlFor="export-game-title" shrink>
                Game Title
              </InputLabel>
              <Input
                id="export-game-title"
                fullWidth
                value={gameTitleOverride}
                placeholder={props.selectedGame?.title}
                onChange={(event) => setGameTitleOverride(event.target.value)}
              />
            </div>
          )}

          <Select
            multiple
            input={<Input />}
            className={classes.languageSelector}
            renderValue={() => {
              if (isAllLanguagesSelected) {
                return "All Languages";
              } else if (selectedLanguages.length === 1) {
                return props.selectedBase.supportedLanguages[selectedLanguages[0]];
              } else if (selectedLanguages.length > 1) {
                return "Multiple Languages";
              } else {
                return "Select Languages";
              }
            }}
            onChange={(event) => {
              setSelectedLanguages(event.target.value);
            }}
            value={selectedLanguages}
          >
            <MenuItem key="All Languages" value="*">
              <Checkbox
                checked={isAllLanguagesSelected}
                onChange={(event) => {
                  if (event.target.checked === true) {
                    const allLanguages = Object.keys(props.selectedBase.supportedLanguages);
                    //console.debug("setting all languages as selected", allLanguages);
                    setSelectedLanguages(allLanguages);
                    setIsAllLanguagesSelected(true);
                  } else if (event.target.checked === false) {
                    setSelectedLanguages([]);
                    setIsAllLanguagesSelected(false);
                  }
                  //console.debug("changed all lang options");
                }}
              />
              <ListItemText primary={"All Languages"} />
              {"All Languages"}
            </MenuItem>
            {Object.entries(props.selectedBase.supportedLanguages).map(([languageKey, languageCaption]) => {
              return (
                <MenuItem key={languageKey} value={languageKey}>
                  <Checkbox checked={selectedLanguages.indexOf(languageKey) > -1} />
                  <ListItemText primary={languageCaption} />
                  {languageCaption}
                </MenuItem>
              );
            })}
          </Select>
        </CardContent>
      </Card>
      <Card className={classes.downloadDialogCard}>
        <CardContent style={{ margin: 0, padding: 0 }}>
          {networkViews.map((network, index, array) => (
            <div key={index}>
              <div className={classes.networkDiv} key={network.name}>
                {/* <Checkbox name={network.name} checked={totalSet.has(network.name)} onChange={changeNetwork}></Checkbox> */}
                <Checkbox name={network.name} onChange={changeNetwork}></Checkbox>
                <Typography style={{ flex: 1 }}>{network.caption}</Typography>
                <img className={classes.networkLogo} src={network.logo} />
              </div>
              {index !== array.length - 1 && (
                <hr style={{ borderWidth: 1, borderColor: "#515151", margin: "0px 10px 0px 10px" }} />
              )}
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className={classes.downloadDialogCard}>
        <CardContent
          style={{
            textAlign: "center",
            margin: 0,
            padding: 5,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress
            color={"secondary"}
            style={{ textAlign: "center", display: isBuilding ? "block" : "none" }}
          ></CircularProgress>
          <Button
            name="download"
            disabled={isBuilding}
            style={{ display: !isBuilding ? "block" : "none", width: "100%" }}
            onClick={() => startExport(false)}
          >
            {buttonText}
          </Button>
          {canDeliverDropbox && (
            <Button
              name="submitToDropbox"
              variant="outlined"
              size="small"
              disabled={isBuilding}
              // Smaller and flatter than Download: submitting is the rarer
              // action of the two and should not compete with it for the eye.
              style={{
                display: !isBuilding ? "block" : "none",
                marginTop: "8px",
                fontSize: "13px",
                color: "#9bcec0",
                borderColor: "#5d7c76",
                textTransform: "none",
                padding: "2px 14px",
              }}
              onClick={() => canStartExport() && setIsSubmitConfirmOpen(true)}
            >
              Submit to Dropbox
            </Button>
          )}
        </CardContent>
      </Card>
      <Typography style={{ textAlign: "center" }}>{error}</Typography>
      <DialogPlayableSizeWarning
        versions={sizeWarningVersions}
        toDropbox={sizeWarningToDropbox}
        onCancel={() => setSizeWarningVersions([])}
        onConfirm={() => {
          setSizeWarningVersions([]);
          runExport(sizeWarningToDropbox, sizeWarningFolder);
        }}
      />
      <DialogDropboxSubmit
        isOpen={isSubmitConfirmOpen}
        defaultCompanyFolder={props.userCompany?.name}
        defaultGameFolder={props.selectedGame?.title}
        gameCode={exportGameTitle}
        destinationFor={(folder) =>
          dropboxDestination(buildProfileData, {
            dropboxFolder: folder,
            gameTitle: exportGameTitle,
            versionName: props.selectedVersion?.title?.replaceAll(" ", "_"),
          })
        }
        onCancel={() => setIsSubmitConfirmOpen(false)}
        onConfirm={(folder) => {
          setIsSubmitConfirmOpen(false);
          startExport(true, folder);
        }}
      />
    </Dialog>
  );
}
